import React, { useState, useEffect } from "react";
import { Box, FormControlLabel, Radio, Typography } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../../components/common/customModal";
import CustomInput from "../../../components/common/inputField";
import CustomSelect from "../../../components/common/customSelect";
import {
  IdeaCapturinfFormData,
  IdeaCapturinfFormErrors,
  IdeathonData,
  REImagingUpdateData,
} from "../../../types/dashboard";
import dashboardService from "../../../services/DashboardServices";
import { showToast } from "../../../components/common/toastNotification";
import { useAppSelector } from "../../../globalState/hooks";
import { LoginRootState } from "../../../types/login";

interface IdeaCapturingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  reImagineUpdateData: REImagingUpdateData;
}
interface OtherDetail {
  id: number;
  value: string;
}
interface Option {
  label: string;
  value: string;
}
const IdeaCapturingModal: React.FC<IdeaCapturingModalProps> = ({
  open,
  onClose,
  onSave,
  reImagineUpdateData,
}) => {
  const [otherDetailsCheckBoxData, setOtherDetailsCheckBoxData] = useState<
    OtherDetail[]
  >([]);

  const [divisionOptions, setDivisionOptions] = useState<Option[]>([]);
  const [functionOptions, setFunctionOptions] = useState<Option[]>([]);
  const [subFunctionOptions, setSubFunctionOptions] = useState<Option[]>([]);
  const [approvalData, setApprovalData] = useState<Option[]>([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isIdeathon, setIsIdeathon] = useState(false);
  const [isIdeathonData, setIsIdeathonData] = useState<IdeathonData>({});

  const [questionList, setQuestionList] = useState<
    { id: number; question: string }[]
  >([]);
  const [answers, setAnswers] = useState<OtherDetail[]>([]);
  const [lastToastMessage, setLastToastMessage] = useState<string>("");

  const [formData, setFormData] = useState<IdeaCapturinfFormData>({
    id: null,
    division: "",
    departmentId: "",
    subFunctionName: "",
    approvalId: "",
    otherDetailsId: "",
    referenceDocument: "",
    ideaDetails: "",
    otherDetailsText: "",
  });

  const [formErrors, setFormErrors] = useState({
    division: "",
    departmentId: "",
    approvalId: "",
    otherDetailsId: "",
    subFunctionName: "",
    ideaDetails: "",
    otherDetailsText: "",
  });

  useEffect(() => {
    const bindFormData = async () => {
      if (
        open &&
        reImagineUpdateData &&
        Object.keys(reImagineUpdateData).length > 0
      ) {
        const {
          id,
          division,
          functionName,
          subFunctionName,
          approvalId,
          referenceDocument,
          ideaDetails,
          otherDetailsId,
          otherData,
        } = reImagineUpdateData;

        // Set division first
        setFormData((prev) => ({
          ...prev,
          id: id ?? null,
          division: division || "",
          departmentId: functionName || "",
          subFunctionName: subFunctionName || "",
          approvalId: approvalId || "",
          referenceDocument: referenceDocument || "",
          ideaDetails: ideaDetails || "",
          otherDetailsId: Array.isArray(otherDetailsId)
            ? otherDetailsId[0]
            : otherDetailsId || "",
          otherDetailsText: otherData || "",
        }));

        if (division) {
          await fetchFunctions(division);
        }

        if (functionName) {
          await fetchSubFunctions(functionName);
        }

        if (subFunctionName) {
          await fetchApprovals(subFunctionName);
        }
      } else if (!open) {
        setFormData({
          id: null,
          division: "",
          departmentId: "",
          subFunctionName: "",
          approvalId: "",
          otherDetailsId: "",
          referenceDocument: "",
          ideaDetails: "",
          otherDetailsText: "",
        });
        setAnswers([]);
      }

      setFormErrors({
        division: "",
        departmentId: "",
        subFunctionName: "",
        approvalId: "",
        otherDetailsId: "",
        ideaDetails: "",
        otherDetailsText: "",
      });
    };

    bindFormData();
  }, [reImagineUpdateData, open]);

  useEffect(() => {
    checkIsIdeathonActive();
    fetchDivisions();
  }, []);
  const checkIsIdeathonActive = async () => {
    try {
      const { data } =
        await dashboardService.REImagineConfigurationIsIdeathon();

      if (data?.success) {
        const active = data.data?.isActive === true;
        setIsIdeathon(active);

        if (active) {
          fetchQuestionList();

          if (data.data?.subFunctionName) {
            const approvals = await fetchApprovals(data.data.subFunctionName);

            const matchedApproval = approvals[0]?.label;
            const matchedApprovalId = approvals[0]?.value;
            setApprovalData(approvals);
            setIsIdeathonData({
              ...data.data,
              approvalName: matchedApproval || "N/A",
              approvalId: matchedApprovalId || "N/A",
            });
          } else {
            setIsIdeathonData(data.data);
          }
        } else {
          setIsIdeathonData(data.data);
          fetchOtherDetailsCheckBoxData();
        }
      }
    } catch (error) {
      console.error("Failed to check Ideathon status:", error);
    }
  };

  // fetch other details checkbox data
  const fetchOtherDetailsCheckBoxData = async (): Promise<void> => {
    try {
      const response = await dashboardService.GetOtherDetailsAPI();

      if (response?.data?.data.length > 0) {
        const formattedData: OtherDetail[] = response.data.data.map(
          (item: { id: number; otherDetailsName: string }) => ({
            id: item.id,
            value: item.otherDetailsName,
          }),
        );

        setOtherDetailsCheckBoxData(formattedData);
      } else {
        setOtherDetailsCheckBoxData([]);
      }
    } catch (error) {
      console.error("Error fetching other details:", error);
    }
  };
  const fetchQuestionList = async () => {
    try {
      const { data } = await dashboardService.getOtherQuestionListAPI();
      if (data?.success && Array.isArray(data.data)) {
        setQuestionList(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch questions", error);
    }
  };
  const handleAnswerChange = (id: number, value: string) => {
    setAnswers((prevAnswers) => {
      const existing = prevAnswers.find((item) => item.id === id);
      if (existing) {
        return prevAnswers.map((item) =>
          item.id === id ? { ...item, value } : item,
        );
      } else {
        return [...prevAnswers, { id, value }];
      }
    });
  };
  const getAnswerValue = (id: number) => {
    return answers.find((item) => item.id === id)?.value || "";
  };
  // fetch divistion list list data
  const fetchDivisions = async () => {
    try {
      const { data } = await dashboardService.getDivisionListAPI();
      if (data?.success) {
        setDivisionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.division,
            value: item.division,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Failed to fetch divisions", error);
    }
  };

  // fetch Department list data
  const fetchFunctions = async (division: string) => {
    try {
      const { data } =
        await dashboardService.GetFunctionListByDivisionIdAPI(division);
      if (data?.success) {
        setFunctionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.functionName,
            value: item.functionName,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Failed to fetch functions", error);
    }
  };

  const fetchSubFunctions = async (func: string) => {
    try {
      const { data } = await dashboardService.GetSubFunctionByFunctionAPI(func);
      if (data?.success) {
        setSubFunctionOptions(
          (data.data as Array<Record<string, string>>)?.map((item) => ({
            label: item.subFunctionName,
            value: item.subFunctionName,
          })) || [],
        );
      }
    } catch (error) {
      console.error("Failed to fetch sub-functions", error);
    }
  };

  const fetchApprovals = async (subFunction: string) => {
    try {
      const { data } =
        await dashboardService.GetApprovalNameBySubFunctionAPI(subFunction);
      if (data?.success) {
        const approvalOptions = (
          data.data as Array<Record<string, string>>
        )?.map((item) => ({
          label: `${item.approvalName}(${item.approvalEmail})`,
          value: item.approvalId,
        }));

        setApprovalData(approvalOptions);

        if (approvalOptions.length === 1) {
          setFormData((prev) => ({
            ...prev,
            approvalId: approvalOptions[0].value,
          }));
        }
        setFormErrors((prev) => ({
          ...prev,
          approvalId: "",
        }));
        return approvalOptions;
      }
    } catch (error) {
      console.error("Error fetching approval list:", error);
    }

    return [];
  };

  //// handle Input Onchange code
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  /// hande Drop down onchange code
  const handleDropDownOnChange = async (event: {
    target: { name: string; value: string | number };
  }) => {
    const { name, value } = event.target;

    if (name === "division") {
      setFormData((prev) => ({
        ...prev,
        division: value.toString(),
        departmentId: "",
        subFunctionName: "",
        approvalId: "",
      }));
      setFunctionOptions([]);
      setSubFunctionOptions([]);
      setApprovalData([]);
      await fetchFunctions(value.toString());
    } else if (name === "departmentId") {
      setFormData((prev) => ({
        ...prev,
        departmentId: value.toString(),
        subFunctionName: "",
        approvalId: "",
      }));
      setSubFunctionOptions([]);
      setApprovalData([]);
      await fetchSubFunctions(value.toString());
    } else if (name === "subFunctionName") {
      setFormData((prev) => ({
        ...prev,
        subFunctionName: value.toString(),
        approvalId: "",
      }));
      setApprovalData([]);
      await fetchApprovals(value.toString());
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  //// handle checkbox onchange code
  const handleRadioChange = (id: number) => {
    setFormData((prevData) => ({
      ...prevData,
      otherDetailsId: id.toString(),
      otherDetailsText: id === 10 ? prevData.otherDetailsText : "",
    }));
  };

  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials,
  );
  //// handle form validation code
  const validateForm = () => {
    const errors: IdeaCapturinfFormErrors = {};

    if (!isIdeathon && !formData.division) {
      errors.division = "Division is required.";
    }
    if (!isIdeathon && !formData.departmentId) {
      errors.departmentId = "Function is required.";
    }
    if (!isIdeathon && !formData.subFunctionName) {
      errors.subFunctionName = "Sub Function Name is required.";
    }
    if (!isIdeathon) {
      if (!formData.approvalId) {
        errors.approvalId = "Approval By is required.";
      }
    }
    if (!isIdeathon && !formData.ideaDetails) {
      errors.ideaDetails = "Provide Details Idea is required.";
    }
    if (!isIdeathon && !formData.otherDetailsId) {
      showToast("Select Your Impact Area Details", "error");
      errors.otherDetailsId = "Select Other Details.";
    }
    if (
      !isIdeathon &&
      formData.otherDetailsId === "10" &&
      !formData.otherDetailsText
    ) {
      const errorMessage = "Please specify your impact area.";
      errors.otherDetailsText = errorMessage;
    }

    const urlPattern =
      /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
    if (isIdeathon) {
      const answeredAtLeastOne = answers.some(
        (a) => a.value && a.value.trim() !== "",
      );

      if (!answeredAtLeastOne) {
        showToast("Please answer at least one question.", "error");
        return false;
      }
    }

    if (formData.referenceDocument) {
      if (!urlPattern.test(formData.referenceDocument)) {
        const errorMessage = "Please enter a valid URL";

        errors.referenceDocument = "Enter a valid reference document URL.";

        if (lastToastMessage !== errorMessage) {
          showToast(errorMessage, "error");
          setLastToastMessage(errorMessage);

          setTimeout(() => {
            setLastToastMessage("");
          }, 3000);
        }
      }
    }

    setFormErrors({
      division: errors.division || "",
      departmentId: errors.departmentId || "",
      subFunctionName: errors.subFunctionName || "",
      approvalId: errors.approvalId || "",
      otherDetailsId: errors.otherDetailsId || "",
      ideaDetails: errors.ideaDetails || "",
      otherDetailsText: errors.otherDetailsText || "",
    });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    if (isIdeathon) {
      const nonEmptyAnswers = answers.filter((a) => a.value.trim() !== "");

      if (nonEmptyAnswers.length === 0) {
        showToast("Please answer at least one question", "error");
        return false;
      }
    }

    return true;
  };

  const handleSaveFormData = (buttonName: string) => {
    handleSaveData(buttonName);
  };
  //// handle add and Update Submit code
  const handleSaveData = async (buttonCheck: string) => {
    setBtnLoading(true);
    if (!validateForm()) {
      setBtnLoading(false);
      return;
    }
    // onSave();
    try {
      const {
        id,
        division,
        departmentId,
        subFunctionName,
        approvalId,
        referenceDocument,
        ideaDetails,
      } = formData;

      setBtnLoading(true);
      const inputData = {
        id,
        division: isIdeathon ? isIdeathonData.division : division,
        functionName: isIdeathon ? isIdeathonData.functionName : departmentId,
        subFunctionName: isIdeathon
          ? isIdeathonData.subFunctionName
          : subFunctionName,
        approvalId: isIdeathon ? isIdeathonData.approvalId : approvalId,
        // approvalName: isIdeathon ? isIdeathonData.approvalName : "",
        referenceDocument,
        ideaDetails,
        isIdeathon: isIdeathon,
        isInitiated: buttonCheck === "Submit" ? true : false,
        status: 1,
        otherDetailsId: isIdeathon ? [] : [formData.otherDetailsId],
        otherData: formData.otherDetailsText || "",
        ideathonData: isIdeathon
          ? answers.map((a) => ({
              questionId: a.id,
              answer: a.value,
            }))
          : [],
      };

      const apiCall = id
        ? dashboardService.updateREImagineDataAPI
        : dashboardService.addREImagineData;

      await apiCall(inputData)
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            setFormData({
              id: null,
              division: "",
              departmentId: "",
              subFunctionName: "",
              approvalId: "",
              otherDetailsId: "",
              referenceDocument: "",
              ideaDetails: "",
            });
            onSave();
            onClose();
            fetchOtherDetailsCheckBoxData();
          } else {
            showToast(response?.data?.message, "error");
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        })
        .finally(() => {
          setBtnLoading(false);
        });
    } catch (error) {
      console.error("Error saving IRefer data:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      onSave={() => {
        handleSaveFormData("Submit");
      }}
      saveTitle="Submit"
      title={isIdeathon ? "Ideathon platform" : "Idea Capturing Platform"}
      width={800}
      button2="Cancel"
      onClickButton2={onClose}
      btnLoading={btnLoading}
      isDraftButtonShow={!isIdeathon}
      onSaveDraft={() => {
        handleSaveFormData("saveDraft");
      }}
    >
      <Box>
        <Typography className="f-14" mb={1} color="error" fontWeight={600}>
          Employee Details{" "}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", fontSize: "12px", display: "flex" }}
            >
              Full Name
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "500",
                fontSize: "12px",
                display: "flex",
                color: "#CCCCCC",
              }}
            >
              {userDetails.fullName.length > 29
                ? userDetails.fullName.substr(0, 29).concat("...")
                : userDetails.fullName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", fontSize: "12px", display: "flex" }}
            >
              Employee Code
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "500",
                fontSize: "12px",
                display: "flex",
                color: "#CCCCCC",
              }}
            >
              {userDetails.userId.length > 29
                ? userDetails.userId.substr(0, 29).concat("...")
                : userDetails.userId}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "medium", fontSize: "12px", display: "flex" }}
            >
              Function Name
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "500",
                fontSize: "12px",
                display: "flex",
                color: "#CCCCCC",
              }}
            >
              {userDetails.department}
            </Typography>
          </Grid>
        </Grid>

        <Typography
          className="f-14"
          mb={1}
          color="error"
          fontWeight={600}
          mt={1}
        >
          {isIdeathon
            ? "Functional Detail"
            : "Functional Detail - Select the division & function for which you are submitting the idea"}
        </Typography>
        <Grid container spacing={2}>
          {isIdeathon ? (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  Division
                </Typography>
                <Typography sx={{ color: "#ccc", fontSize: "14px" }}>
                  {isIdeathonData.division || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  Function Name
                </Typography>
                <Typography sx={{ color: "#ccc", fontSize: "14px" }}>
                  {isIdeathonData.functionName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  Sub Function
                </Typography>
                <Typography sx={{ color: "#ccc", fontSize: "14px" }}>
                  {isIdeathonData.subFunctionName || "N/A"}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  Approval
                </Typography>
                <Typography sx={{ color: "#ccc", fontSize: "14px" }}>
                  {isIdeathonData.approvalName || "N/A"}
                </Typography>
              </Grid> */}
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  name="division"
                  label="Division"
                  placeHolder="Select Division"
                  onChange={handleDropDownOnChange}
                  options={divisionOptions}
                  value={formData.division}
                  error={formErrors.division}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  name="departmentId"
                  label="Function Name"
                  placeHolder="Select Function"
                  onChange={handleDropDownOnChange}
                  options={functionOptions}
                  value={formData.departmentId}
                  error={formErrors.departmentId}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  name="subFunctionName"
                  label="Sub Function"
                  placeHolder="Select Sub Function"
                  onChange={handleDropDownOnChange}
                  options={subFunctionOptions}
                  value={formData.subFunctionName}
                  error={formErrors.subFunctionName}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  name="approvalId"
                  label="Approval"
                  placeHolder="Select Approval"
                  onChange={handleDropDownOnChange}
                  options={approvalData}
                  value={formData.approvalId}
                  error={formErrors.approvalId}
                  required
                  readOnly
                />
              </Grid>
            </>
          )}
        </Grid>
        {isIdeathon && (
          <>
            <Typography
              className="f-14"
              mb={1}
              color="error"
              fontWeight={600}
              mt={1}
            >
              About Flying Flea X Ideathon
            </Typography>

            <Typography className="f-12" mb={1} mt={1}>
              The Flying Flea Ideathon invites ideas focused on shaping the
              future of Royal Enfield&apos;s EV motorcycle by deeply
              understanding rider expectations and market opportunities.
            </Typography>
            <Typography className="f-12" mb={1} mt={1}>
              As the EV ecosystem continues to evolve, this Ideathon aims to
              gather insights that can support stronger market positioning,
              customer engagement, and sales narratives for Flying Flea. The
              focus is on shaping a compelling value proposition that resonates
              with new-age riders while strengthening the brand’s presence in
              the EV motorcycle space.
            </Typography>
          </>
        )}
        <Typography
          className="f-14"
          mb={1}
          color="error"
          fontWeight={600}
          mt={1}
        >
          {isIdeathon ? "Your Idea" : "Impact Areas"}
        </Typography>

        <Grid container spacing={2}>
          {isIdeathon
            ? questionList.map((q) => (
                <Grid item xs={12} key={q.id}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, mb: 0.5, color: "#fff" }}
                  >
                    {q.question}
                  </Typography>
                  <CustomInput
                    name={`question-${q.id}`}
                    placeholder="Write Answer"
                    type="text"
                    value={getAnswerValue(q.id)}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                  />
                </Grid>
              ))
            : otherDetailsCheckBoxData.map((item) => (
                <Grid item xs={12} sm={6} key={item.id}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={formData.otherDetailsId === item.id.toString()}
                        onChange={() => handleRadioChange(item.id)}
                        sx={{
                          padding: "5px 10px",
                          color: "#FFFFFF",
                          "&.Mui-checked": {
                            color: "red",
                          },
                        }}
                      />
                    }
                    label={item.value}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        fontSize: "12px",
                        flex: 1,
                      },
                    }}
                  />
                </Grid>
              ))}
        </Grid>
        {formData.otherDetailsId === "10" && (
          <Grid item xs={12} style={{ marginTop: "10px" }}>
            <CustomInput
              name="otherDetailsText"
              placeholder="Please specify your impact area"
              type="text"
              value={formData.otherDetailsText}
              onChange={handleInputChange}
              error={formErrors.otherDetailsText}
              required
            />
          </Grid>
        )}

        {!isIdeathon && (
          <>
            <Typography
              className="f-14"
              mb={1}
              color="error"
              fontWeight={600}
              mt={1}
            >
              Your Idea
            </Typography>
            <Grid item xs={12}>
              <CustomInput
                label=""
                name="ideaDetails"
                placeholder="Enter the Detail about your idea"
                type="text"
                onChange={handleInputChange}
                value={formData.ideaDetails}
                error={formErrors.ideaDetails}
                required
              />
            </Grid>
          </>
        )}
        <Grid container spacing={2} style={{ marginTop: "0px" }}>
          <Grid item xs={12} md={12}>
            <Typography
              className="f-14"
              mb={1}
              color="error"
              fontWeight={600}
              mt={1}
            >
              Reference Document (If Any)
            </Typography>
            <CustomInput
              label=""
              name="referenceDocument"
              placeholder="Please enter URL"
              type="text"
              onChange={handleInputChange}
              value={formData.referenceDocument}
            />
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <CustomInput
              label="Remarks"
              name="remarks"
              placeholder="Enter Remarks"
              type="text"
              onChange={handleInputChange}
              value={formData.remarks}
              error={formErrors.remarks}
              required
            />
          </Grid> */}
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default IdeaCapturingModal;
